/**
 * Business Partner (employer) portal client-side RBAC, Phase 1.
 *
 * Mirrors stargate-backend `Business_Partner/employer_portal_access_control.py`.
 * RBAC is always enabled.
 *
 * Persona is read from the Cognito JWT (`state.auth.access['custom:UserType']`),
 * which is the same shape both portals already consume for `custom:user_portal`.
 */

const EMPLOYER_PORTAL_RBAC_ENABLED = true;

// Canonical user_type strings stored in SRMUserManagement.user_type and
// mirrored on Cognito custom:UserType. Keep in lock-step with the backend
// constants in Business_Partner/employer_portal_access_control.py.
export const EMPLOYER_PORTAL_USER_TYPE_STRING_BUSINESS_PARTNER_ADMINISTRATOR =
  'Business Partner Administrator';
export const EMPLOYER_PORTAL_USER_TYPE_STRING_ANALYST = 'Analyst';
export const EMPLOYER_PORTAL_USER_TYPE_STRING_JOBS_MANAGER = 'Jobs Manager';

// Module identifiers used by the navigation filter and the route guard.
// These intentionally match the backend `EMPLOYER_PORTAL_MODULE_*` values.
export const EMPLOYER_PORTAL_MODULE_JOBS = 'jobs';
export const EMPLOYER_PORTAL_MODULE_APPRENTICESHIPS = 'apprenticeships';
export const EMPLOYER_PORTAL_MODULE_OPPORTUNITIES = 'opportunities';
export const EMPLOYER_PORTAL_MODULE_EVENTS = 'events';
export const EMPLOYER_PORTAL_MODULE_TALENT_EXCHANGE = 'talent_exchange';
export const EMPLOYER_PORTAL_MODULE_EMERGING_TALENT = 'emerging_talent';
export const EMPLOYER_PORTAL_MODULE_TALENT_READY = 'talent_ready';
export const EMPLOYER_PORTAL_MODULE_USER_MANAGEMENT = 'user_management';
export const EMPLOYER_PORTAL_MODULE_COMPANY_SETTINGS = 'company_settings';
export const EMPLOYER_PORTAL_MODULE_DASHBOARD = 'dashboard';
export const EMPLOYER_PORTAL_MODULE_OPPORTUNITY_LINK = 'opportunity_link';

const EMPLOYER_PORTAL_MODULES_ALLOWED_FOR_JOBS_MANAGER = new Set([
  EMPLOYER_PORTAL_MODULE_JOBS,
  EMPLOYER_PORTAL_MODULE_APPRENTICESHIPS,
]);

/**
 * Read the effective employer-portal persona string for the logged-in user.
 *
 * Resolution order:
 *  1. Cognito IdToken claim `custom:UserType` (already spread into
 *     `state.auth.access` by `auth.js`). This is the fastest path and is
 *     populated for new BP users by the backend's unconditional Cognito
 *     sync (`sync_cognito_user_type_for_employer_portal_user`).
 *  2. DB-backed `state.auth.userType` (set from the login response body
 *     by `auth.js`, which itself comes from `GetUserTokenDetails` reading
 *     `SRMUserManagement.user_type`). Used as a safe fallback for users
 *     created before the unconditional sync shipped, or whenever the
 *     Cognito attribute is unexpectedly missing.
 *
 * Returns `null` only when both sources are empty. Non-Jobs-Manager
 * personas (including legacy `Business Administrator`) are not restricted.
 */
export function selectEffectiveEmployerPortalUserTypeStringFromAuthState(
  state
) {
  const access = (state && state.auth && state.auth.access) || {};
  const cognitoClaim = access['custom:UserType'];
  if (cognitoClaim) {
    return cognitoClaim;
  }
  const dbBackedUserType = state && state.auth && state.auth.userType;
  return dbBackedUserType || null;
}

/**
 * True when RBAC is on AND the user may use the company-page creation flow.
 * Only Jobs Manager is blocked (Hang tight until an admin publishes).
 * All other personas — including legacy `Business Administrator` — are
 * treated as admin for this flow.
 */
export function selectIsEmployerPortalUserAllowedOnCompanyPageCreationFlow(
  state
) {
  const featureFlagEnabled = EMPLOYER_PORTAL_RBAC_ENABLED;
  if (!featureFlagEnabled) {
    return false;
  }
  const effectiveUserTypeString = selectEffectiveEmployerPortalUserTypeStringFromAuthState(
    state
  );
  return (
    effectiveUserTypeString !== EMPLOYER_PORTAL_USER_TYPE_STRING_JOBS_MANAGER
  );
}

/**
 * True when RBAC is enabled AND the caller is explicitly Jobs Manager.
 * Every other user_type (legacy admin, analyst, unknown) keeps full modules.
 */
export function isEmployerPortalUserRestrictedToJobsManagerModules(
  featureFlagEnabled,
  effectiveUserTypeString
) {
  if (!featureFlagEnabled) {
    return false;
  }
  return (
    effectiveUserTypeString === EMPLOYER_PORTAL_USER_TYPE_STRING_JOBS_MANAGER
  );
}

/**
 * Convenience: read both the flag and the user_type out of redux state
 * and return the restricted boolean. Used by Main.js / Routes.js.
 */
export function selectIsEmployerPortalUserRestrictedToJobsManagerModules(
  state
) {
  const featureFlagEnabled = EMPLOYER_PORTAL_RBAC_ENABLED;
  if (!featureFlagEnabled) {
    return false;
  }
  const effectiveUserTypeString = selectEffectiveEmployerPortalUserTypeStringFromAuthState(
    state
  );
  return isEmployerPortalUserRestrictedToJobsManagerModules(
    featureFlagEnabled,
    effectiveUserTypeString
  );
}

/**
 * Returns true if the given navigation menu item should be visible to
 * the current user. `menuItem.moduleIdentifier` is matched against the
 * Jobs Manager allowlist. Menu items without an identifier are treated
 * as legacy and shown only when the user is NOT restricted.
 */
export function isEmployerPortalMenuItemVisibleForRestrictedFlag(
  menuItem,
  isUserRestrictedToJobsManagerModules
) {
  if (!isUserRestrictedToJobsManagerModules) {
    return true;
  }
  const moduleIdentifier = menuItem && menuItem.moduleIdentifier;
  if (!moduleIdentifier) {
    return false;
  }
  return EMPLOYER_PORTAL_MODULES_ALLOWED_FOR_JOBS_MANAGER.has(moduleIdentifier);
}

/**
 * Returns the filtered subset of the given menu list given the
 * `featureFlagEnabled` / `effectiveUserTypeString` pair.
 */
export function filterEmployerPortalMenuItemsForUser(
  menuItems,
  {featureFlagEnabled, effectiveUserTypeString}
) {
  if (!Array.isArray(menuItems)) {
    return menuItems;
  }
  const isUserRestrictedToJobsManagerModules = isEmployerPortalUserRestrictedToJobsManagerModules(
    featureFlagEnabled,
    effectiveUserTypeString
  );
  return menuItems.filter(menuItem =>
    isEmployerPortalMenuItemVisibleForRestrictedFlag(
      menuItem,
      isUserRestrictedToJobsManagerModules
    )
  );
}

/**
 * Route guard for stargatesrm `apps/employerApp/.../Routes.js`. Returns
 * true when the user is restricted and the route is NOT one of the
 * Jobs Manager allowed routes (Jobs, Apprenticeships, and the implicit
 * default redirect target).
 */
export function shouldRedirectEmployerPortalUserAwayFromRoute(
  routeModuleIdentifier,
  isUserRestrictedToJobsManagerModules
) {
  if (!isUserRestrictedToJobsManagerModules) {
    return false;
  }
  if (!routeModuleIdentifier) {
    return true;
  }
  return !EMPLOYER_PORTAL_MODULES_ALLOWED_FOR_JOBS_MANAGER.has(
    routeModuleIdentifier
  );
}

/**
 * Role options shown by the AddUser dialog, dependent on the flag.
 * `submitValue` is the exact string sent to the backend as `user_type`
 * (and mirrored to Cognito `custom:UserType`).
 */
export const EMPLOYER_PORTAL_USER_TYPE_OPTIONS_WHEN_FEATURE_DISABLED = [
  {
    label: 'Administrator',
    submitValue: EMPLOYER_PORTAL_USER_TYPE_STRING_BUSINESS_PARTNER_ADMINISTRATOR,
    description:
      'This role manages everything on the portal. It’s the only role that can edit the company page and manage all admins.',
  },
  {
    label: 'Analyst',
    submitValue: EMPLOYER_PORTAL_USER_TYPE_STRING_ANALYST,
    description:
      'This role only views and exports analytics in the portal and will have limited access.',
  },
];

export const EMPLOYER_PORTAL_USER_TYPE_OPTIONS_WHEN_FEATURE_ENABLED = [
  {
    label: 'Administrator',
    submitValue: EMPLOYER_PORTAL_USER_TYPE_STRING_BUSINESS_PARTNER_ADMINISTRATOR,
    description:
      'This role manages everything on the portal. It’s the only role that can edit the company page and manage all admins.',
  },
  {
    label: 'Analyst',
    submitValue: EMPLOYER_PORTAL_USER_TYPE_STRING_ANALYST,
    description:
      'This role only views and exports analytics in the portal and will have limited access.',
  },
  {
    label: 'Jobs Manager',
    submitValue: EMPLOYER_PORTAL_USER_TYPE_STRING_JOBS_MANAGER,
    description:
      'This role can only add and manage Jobs and Apprenticeships. They cannot see other modules on the portal.',
  },
];

export function getEmployerPortalUserTypeOptionsForFlagState(
  featureFlagEnabled
) {
  return featureFlagEnabled
    ? EMPLOYER_PORTAL_USER_TYPE_OPTIONS_WHEN_FEATURE_ENABLED
    : EMPLOYER_PORTAL_USER_TYPE_OPTIONS_WHEN_FEATURE_DISABLED;
}
